import { type ComponentType, type LazyExoticComponent, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'

import { getClient as getHttpClient } from '@molecule/app-http'
import { MoleculeProvider, useStoragePreference } from '@molecule/app-react'
import { getProvider as getStorageProvider } from '@molecule/app-storage'

import { authClient, i18nProvider, themeProvider } from './bonds/index.js'
import { ensureUiProviders } from './bonds/ui-loader.js'
import { AppDialogsHost } from './components/AppDialogs.js'
import { AuthGuard } from './components/AuthGuard.js'
import { ErrorBoundary } from './components/ErrorBoundary.js'
import { GlobalAuthModalGate } from './components/GlobalAuthModalGate.js'
import { HealthBanner } from './components/HealthBanner.js'
import { NotificationBanner } from './components/NotificationBanner.js'
import { OAuthCallback } from './components/OAuthCallback.js'
import { PromptMorphOverlay } from './components/PromptMorphOverlay.js'
import { ScrollManager } from './components/ScrollManager.js'
import { EmailVerificationSync } from './hooks/useEmailVerificationSync.js'
import { lazyWithReload } from './lib/chunk-reload.js'
import { LAST_PROJECT_KEY, parseLastProject } from './lib/last-project.js'
import Landing from './pages/Landing.js'

// ClassMap / styling bonds are deferred out of the landing entry (bonds/ui.ts).
// Wire them once, before any non-landing route's page module loads, so every
// ClassMap-using component has its provider ready by first render. The shared
// loader (bonds/ui-loader.ts) keeps tailwind-merge + the ClassMap styles in a
// separate chunk; the ambient banners load the same chunk on demand via
// useUiProviders when they have something to show on a not-yet-wired route.

/**
 * Lazy route that wires the UI/ClassMap bonds before resolving the page, and
 * waits out a stale-chunk reload instead of crashing (lib/chunk-reload.ts).
 */
function lazyPage(
  loader: () => Promise<{ default: ComponentType<Record<string, never>> }>,
): LazyExoticComponent<ComponentType<Record<string, never>>> {
  return lazyWithReload(() => ensureUiProviders().then(loader))
}

// Landing is the entry route and stays eager so it paints with no chunk fetch
// (and uses hand-written CSS, no ClassMap — so the UI bonds above never load
// for a visitor who only sees the landing). Every other route is lazy, so a
// first-time visitor downloads only React, the router, and Landing instead of
// the entire app. WorkspaceRoute (not Workspace directly) is the IDE boundary:
// it wires the IDE-only bonds and provides the editor/chat/workspace/preview
// contexts within its chunk, so none of that ships in the landing entry.
const Workspace = lazyPage(() =>
  import('./pages/WorkspaceRoute.js').then((m) => ({ default: m.WorkspaceRoute })),
)
const Dashboard = lazyPage(() =>
  import('./pages/Dashboard.js').then((m) => ({ default: m.Dashboard })),
)
const Analytics = lazyPage(() =>
  import('./pages/Analytics.js').then((m) => ({ default: m.Analytics })),
)
const PlatformAnalytics = lazyPage(() =>
  import('./pages/PlatformAnalytics.js').then((m) => ({ default: m.PlatformAnalytics })),
)
const FleetBoxSystem = lazyPage(() =>
  import('./pages/FleetBoxSystem.js').then((m) => ({ default: m.FleetBoxSystemPage })),
)
const PlanUpdated = lazyPage(() =>
  import('./pages/PlanUpdated.js').then((m) => ({ default: m.PlanUpdated })),
)
const Login = lazyPage(() => import('./pages/Login.js').then((m) => ({ default: m.Login })))
const Signup = lazyPage(() => import('./pages/Signup.js').then((m) => ({ default: m.Signup })))
const ForgotPassword = lazyPage(() =>
  import('./pages/ForgotPassword.js').then((m) => ({ default: m.ForgotPassword })),
)
const ResetPassword = lazyPage(() =>
  import('./pages/ResetPassword.js').then((m) => ({ default: m.ResetPassword })),
)
const VerifyEmail = lazyPage(() =>
  import('./pages/VerifyEmail.js').then((m) => ({ default: m.VerifyEmail })),
)
const CliAuthorize = lazyPage(() =>
  import('./pages/CliAuthorize.js').then((m) => ({ default: m.CliAuthorize })),
)
const AcceptInvite = lazyPage(() =>
  import('./pages/AcceptInvite.js').then((m) => ({ default: m.AcceptInvite })),
)
const Pricing = lazyPage(() => import('./pages/Pricing.js').then((m) => ({ default: m.Pricing })))
const SharedProject = lazyPage(() =>
  import('./pages/SharedProject.js').then((m) => ({ default: m.SharedProject })),
)
// One shell for /packages, /packages/:name, /templates and /templates/:slug:
// the same element on all four routes, so React keeps it mounted (search text,
// list, scroll) while a document opens beside it.
const Catalog = lazyPage(() =>
  import('./pages/catalog/CatalogLayout.js').then((m) => ({ default: m.CatalogLayout })),
)
const Terms = lazyPage(() => import('./pages/legal/Terms.js').then((m) => ({ default: m.Terms })))
const Privacy = lazyPage(() =>
  import('./pages/legal/Privacy.js').then((m) => ({ default: m.Privacy })),
)
const Dpa = lazyPage(() => import('./pages/legal/Dpa.js').then((m) => ({ default: m.Dpa })))
const Licensing = lazyPage(() =>
  import('./pages/Licensing.js').then((m) => ({ default: m.Licensing })),
)
const Security = lazyPage(() =>
  import('./pages/Security.js').then((m) => ({ default: m.Security })),
)
const MlclPage = lazyPage(() =>
  import('./pages/MlclPage.js').then((m) => ({ default: m.MlclPage })),
)
const DocPage = lazyPage(() =>
  import('./pages/docs/DocPage.js').then((m) => ({ default: m.DocPage })),
)
const Changelog = lazyPage(() =>
  import('./pages/Changelog.js').then((m) => ({ default: m.Changelog })),
)
const Showcase = lazyPage(() =>
  import('./pages/Showcase.js').then((m) => ({ default: m.Showcase })),
)
const NotFound = lazyPage(() =>
  import('./pages/NotFound.js').then((m) => ({ default: m.NotFound })),
)

/** Redirect /workspace to the last opened project, or create a new one. */
function WorkspaceRedirect() {
  // Subscribed, not read once: a synchronous provider answers on the first
  // render (unchanged behaviour), and an asynchronous one converges here
  // instead of sending the user to a brand-new project they never asked for.
  const { value: storedLastProject, ready } = useStoragePreference<unknown>(LAST_PROJECT_KEY)
  const lastProject = parseLastProject(storedLastProject)
  if (lastProject === null && !ready) return null
  return <Navigate to={lastProject ? `/workspace/${lastProject}` : '/workspace/new'} replace />
}

export function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <MoleculeProvider
          auth={authClient}
          http={getHttpClient()}
          theme={themeProvider}
          storage={getStorageProvider()}
          i18n={i18nProvider}
        >
          {/* Completes the user-OAuth login wherever the provider's bare-origin
              redirect lands (it lands on `/` = Landing, which mounts no auth UI).
              Renders nothing unless this load is an OAuth callback. */}
          <OAuthCallback />
          {/* Picks up an email confirmation done in ANOTHER tab (or another
              browser) so this tab's "Unverified" badge flips without a reload. */}
          <EmailVerificationSync />
          <HealthBanner />
          <NotificationBanner />
          {/* On the authenticated app pages outside the IDE (Dashboard usage
              banner, the global limit toast), open the auth/upgrade modal in
              place instead of navigating to /signup,/login,/pricing. The IDE
              (Workspace) mounts its own; this covers everywhere else. */}
          <GlobalAuthModalGate />
          <AppDialogsHost />
          {/* Rendered above Suspense so the morphing prompt textarea persists
              across the route change + the workspace's code-split load. */}
          <PromptMorphOverlay />
          {/* Route scroll contract: new navigation lands at the top, back/forward
              (and later returns, per-tab) restore the remembered position. */}
          <ScrollManager />
          <Suspense fallback={null}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* Signup email-confirmation landing (PRD9) — the destination of
                  the confirmation email's 24h link; POSTs the token to
                  /api/users/verify-email and renders the outcome. */}
              <Route path="/verify-email" element={<VerifyEmail />} />
              {/* Browser half of `mlcl login` — device-code approval. */}
              <Route path="/cli" element={<CliAuthorize />} />
              {/* Team-invitation landing (SOC2) — the destination of the invite
                  email's 7-day link. Public: a brand-new invitee has no account
                  yet, and the page routes them to sign up before it joins them. */}
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/pricing" element={<Pricing />} />
              {/* Public read-only recipient view for a minted share link (SOC3). */}
              <Route path="/share/:slug" element={<SharedProject />} />
              <Route path="/templates" element={<Catalog />} />
              <Route path="/templates/:slug" element={<Catalog />} />
              {/* Build-time page families (scripts/generate-template-pages.mjs,
                  generate-package-pages.mjs): prerendered in Node from their
                  JSON documents, with a data island the SPA boots from. */}
              <Route path="/packages" element={<Catalog />} />
              <Route path="/packages/:name" element={<Catalog />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/dpa" element={<Dpa />} />
              <Route path="/licensing" element={<Licensing />} />
              <Route path="/security" element={<Security />} />
              {/* The official mlcl CLI page — the npm `homepage` front door for
                  the mlcl package and the www.mlcl.dev redirect target. */}
              <Route path="/mlcl" element={<MlclPage />} />
              <Route path="/docs" element={<DocPage />} />
              <Route path="/docs/:slug" element={<DocPage />} />
              {/* NO /blog ROUTE, deliberately. /blog is a separate STATIC site —
                  the markdown blog generator Synthase built on molecule.dev —
                  committed as real files under app/public/blog/ and served at
                  /blog/… by Netlify, which beats this SPA's catch-all. Adding a
                  React route back here would shadow it on client-side navigation.
                  See app/scripts/stage-static-blog.mjs. */}
              {/* Build-time content pages (scripts/generate-*.mjs). */}
              <Route path="/changelog" element={<Changelog />} />
              <Route path="/showcase" element={<Showcase />} />

              {/* Public full-screen routes */}
              <Route path="/workspace" element={<WorkspaceRedirect />} />
              <Route path="/workspace/:projectId" element={<Workspace />} />

              {/* Guest-or-auth routes: a guest session (mol_guest_uid) can list
                  and reopen its own projects — kicking guests to /login here
                  orphaned the project they just created. */}
              <Route element={<AuthGuard allowGuest />}>
                <Route path="/dashboard" element={<Dashboard />} />
              </Route>
              {/* Auth-only routes */}
              <Route element={<AuthGuard />}>
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/analytics/platform" element={<PlatformAnalytics />} />
                <Route path="/analytics/platform/fleet/:name" element={<FleetBoxSystem />} />
                {/* The standalone billing page was folded into the user
                    settings panel (Billing + Usage sections) and the in-IDE
                    upgrade modal; old links land on the dashboard. */}
                <Route path="/billing" element={<Navigate to="/dashboard" replace />} />
                <Route path="/plan-updated" element={<PlanUpdated />} />
              </Route>

              {/* Catch-all 404 — an unknown path used to render a blank page. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </MoleculeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
